import { useEffect, useMemo, type FunctionComponent } from "react";
import { Alert, Button, Segmented, Select, Space, Tag, Typography } from "antd";
import { CloseOutlined } from "@ant-design/icons";

import { teamActions } from "@fitness-app/app-store";
import { TraineeStatus } from "@fitness-app/data-models/entities/Trainee";

import { useTagOptions } from "~/hooks/useTagOptions";
import { useProductTaskAutomation } from "~/modules/Products/hooks/useProductTaskAutomation";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

import { TraineeSegmentsBar } from "./TraineeSegmentsBar";
import { useTraineeTargeting } from "./useTraineeTargeting";
import { type TraineeTargetSpec } from "./types";

const STATUS_LABELS: Record<TraineeStatus, string> = {
  [TraineeStatus.ACTIVE]: "Aktywni",
  [TraineeStatus.INACTIVE]: "Nieaktywni",
  [TraineeStatus.TERMINATED]: "Zakończeni",
  [TraineeStatus.ARCHIVED]: "Zarchiwizowani",
};

type Props = {
  value: TraineeTargetSpec;
  onChange: (next: TraineeTargetSpec) => void;
};

const labelStyle = { display: "block", marginBottom: 4 } as const;

// A product filter can resolve to thousands of trainees; rendering a row each locks up the modal.
// The exact total is already on the alert above, so the list is only a spot-check of the audience.
const PREVIEW_LIMIT = 100;

export const TraineeTargetingPanel: FunctionComponent<Props> = ({ value, onChange }) => {
  const dispatch = useAppDispatch();
  // The tag dictionary lives on `trainer_config`, which every module loads for itself — and
  // Challenges / Habits never did, so the tag pickers rendered "Brak danych" for anyone who opened
  // them without passing through Klientki first (TRE-2037). Fetch it here instead of in each modal.
  const trainerConfig = useAppSelector((state) => state.team.trainerConfig);
  const { options: tagOptions } = useTagOptions("traineeTags");

  useEffect(() => {
    if (!trainerConfig) {
      void dispatch(teamActions.fetchTrainerConfig());
    }
  }, [trainerConfig, dispatch]);

  const { productWithPrices } = useProductTaskAutomation();
  const { trainees, resolved, resolvedCount, isLoading } = useTraineeTargeting(value);

  const patch = (partial: Partial<TraineeTargetSpec>) => onChange({ ...value, ...partial });

  const productOptions = useMemo(
    () => productWithPrices.map((product) => ({ value: product.id, label: product.name })),
    [productWithPrices],
  );

  const statusOptions = useMemo(
    () => Object.values(TraineeStatus).map((status) => ({ value: status, label: STATUS_LABELS[status] })),
    [],
  );

  const resolvedIds = useMemo(() => new Set(resolved.map((trainee) => trainee.id)), [resolved]);

  // Trainees not currently in the resolved set — candidates for manual add.
  const manualAddOptions = useMemo(
    () =>
      trainees
        .filter((trainee) => !resolvedIds.has(trainee.id))
        .map((trainee) => ({
          value: trainee.id,
          label: `${trainee.firstName} ${trainee.lastName ?? ""}`.trim() + (trainee.email ? ` (${trainee.email})` : ""),
        })),
    [trainees, resolvedIds],
  );

  const positivesActive = (value.productIds.length ? 1 : 0) + (value.includeTags.length ? 1 : 0);

  const textFilter = (input: string, option?: { label?: string }) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const addManually = (id: string) => {
    patch({
      manualIds: value.manualIds.includes(id) ? value.manualIds : [...value.manualIds, id],
      excludedIds: value.excludedIds.filter((excluded) => excluded !== id),
    });
  };

  const removeResolved = (id: string) => {
    if (value.manualIds.includes(id)) {
      patch({ manualIds: value.manualIds.filter((manual) => manual !== id) });
    } else {
      patch({ excludedIds: [...value.excludedIds, id] });
    }
  };

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <TraineeSegmentsBar currentSpec={value} onLoad={onChange} />

      <div>
        <Typography.Text strong style={labelStyle}>
          Klienci posiadający aktywny produkt
        </Typography.Text>
        <Select
          mode="multiple"
          allowClear
          placeholder="Dowolny produkt (pomiń, aby nie filtrować)"
          style={{ width: "100%" }}
          maxTagCount={8}
          options={productOptions}
          value={value.productIds}
          onChange={(productIds: string[]) => patch({ productIds })}
          filterOption={textFilter}
        />
      </div>

      <div>
        <Typography.Text strong style={labelStyle}>
          Klienci z tagiem
        </Typography.Text>
        <Select
          mode="multiple"
          allowClear
          placeholder="Dowolny tag (pomiń, aby nie filtrować)"
          style={{ width: "100%" }}
          maxTagCount={8}
          options={tagOptions}
          value={value.includeTags}
          onChange={(includeTags: string[]) => patch({ includeTags })}
        />
      </div>

      <div>
        <Typography.Text strong style={labelStyle}>
          Pomiń klientów z tagiem
        </Typography.Text>
        <Select
          mode="multiple"
          allowClear
          placeholder="Bez wykluczeń"
          style={{ width: "100%" }}
          maxTagCount={8}
          options={tagOptions}
          value={value.excludeTags}
          onChange={(excludeTags: string[]) => patch({ excludeTags })}
        />
      </div>

      <div>
        <Typography.Text strong style={labelStyle}>
          Ogranicz do statusu
        </Typography.Text>
        <Select
          mode="multiple"
          allowClear
          placeholder="Dowolny status"
          style={{ width: "100%" }}
          options={statusOptions}
          value={value.statuses}
          onChange={(statuses: TraineeStatus[]) => patch({ statuses })}
        />
      </div>

      {positivesActive >= 2 && (
        <Space>
          <Typography.Text>Łącz produkt i tag przez:</Typography.Text>
          <Segmented
            value={value.combinator}
            onChange={(combinator) => patch({ combinator: combinator as TraineeTargetSpec["combinator"] })}
            options={[
              { label: "Dowolny (LUB)", value: "OR" },
              { label: "Wszystkie (I)", value: "AND" },
            ]}
          />
        </Space>
      )}

      <Alert
        type={resolvedCount > 0 ? "info" : "warning"}
        showIcon
        message={
          isLoading
            ? "Liczenie odbiorców..."
            : resolvedCount > 0
              ? `Dopasowano ${resolvedCount} ${resolvedCount === 1 ? "klienta" : "klientów"}`
              : "Brak dopasowanych klientów — wybierz filtr lub dodaj ręcznie."
        }
      />

      <div>
        <Typography.Text strong style={labelStyle}>
          Dodaj klienta ręcznie
        </Typography.Text>
        <Select
          showSearch
          placeholder="Wyszukaj klienta..."
          style={{ width: "100%" }}
          value={null}
          options={manualAddOptions}
          onChange={(id: string) => id && addManually(id)}
          filterOption={textFilter}
        />
      </div>

      {resolved.length > 0 && (
        <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #f0f0f0", borderRadius: 8, padding: 8 }}>
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            {resolved.slice(0, PREVIEW_LIMIT).map((trainee) => (
              <div
                key={trainee.id}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}
              >
                <Space size={6}>
                  <Typography.Text>
                    {trainee.firstName} {trainee.lastName ?? ""}
                  </Typography.Text>
                  {trainee.email ? (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {trainee.email}
                    </Typography.Text>
                  ) : null}
                  {value.manualIds.includes(trainee.id) ? <Tag color="blue">ręcznie</Tag> : null}
                </Space>
                <Button size="small" type="text" icon={<CloseOutlined />} onClick={() => removeResolved(trainee.id)} />
              </div>
            ))}
            {resolved.length > PREVIEW_LIMIT && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                …i {resolved.length - PREVIEW_LIMIT} kolejnych — zapisane zostaną wszystkie {resolved.length}.
              </Typography.Text>
            )}
          </Space>
        </div>
      )}

      {value.excludedIds.length > 0 && (
        <Button type="link" size="small" style={{ paddingLeft: 0 }} onClick={() => patch({ excludedIds: [] })}>
          Przywróć {value.excludedIds.length} wykluczonych
        </Button>
      )}
    </Space>
  );
};

export default TraineeTargetingPanel;
